import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Check, Inbox, Plus } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { ocrImage, parseReceiptText } from "../lib/receiptOcr";
import { shrink } from "./Receipts";
import ReceiptCrop from "./ReceiptCrop";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

// Camera → crop → read → save to the Receipt inbox
export function SnapDialog({ onClose }) {
  const { addToInbox } = useStore();
  const [raw, setRaw] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [f, setF] = useState({ amount: "", date: "", store: "", note: "" });
  const [saved, setSaved] = useState(0);
  const cam = useRef(null);
  const reset = () => { setRaw(null); setFile(null); setPreview(null); setF({ amount: "", date: "", store: "", note: "" }); };
  const pick = async (e) => {
    const p = e.target.files?.[0];
    e.target.value = "";
    if (p) setRaw(await shrink(p));
  };
  const read = async (img) => {
    setRaw(null);
    setFile(img);
    setPreview(URL.createObjectURL(img));
    setProgress(0);
    try {
      const r = parseReceiptText(await ocrImage(img, setProgress));
      setF((x) => ({ ...x, amount: r.amount ? r.amount.toFixed(2) : "", date: r.date || "", store: r.store || "" }));
    } catch {
      toast("Couldn't read the receipt — type the total and date");
    }
    setProgress(null);
  };
  const save = async () => {
    try {
      await addToInbox(file, f);
      setSaved((n) => n + 1);
      toast.success("Saved to your Receipt inbox");
      reset();
    } catch {
      toast.error("Couldn't save the photo — the device may be out of space");
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="snap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5 text-primary" /> Snap a receipt</DialogTitle>
          <DialogDescription>Photos go to your Receipt inbox. Match them here, or send them to your computer by AirDrop or email.</DialogDescription>
        </DialogHeader>
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="snap-camera-input" />
        {raw ? <ReceiptCrop file={raw} onDone={read} /> : !file ? (
          <div className="space-y-3">
            <Button className="w-full h-24 text-lg rounded-2xl" onClick={() => cam.current?.click()} data-testid="snap-camera-button"><Camera className="h-6 w-6 mr-2" /> {saved ? "Snap another receipt" : "Open camera"}</Button>
            {saved > 0 && <p className="text-sm text-center text-income flex items-center justify-center gap-1" data-testid="snap-saved-count"><Check className="h-4 w-4" /> {saved} saved this session</p>}
            <Button variant="outline" className="w-full" asChild><Link to="/receipts" onClick={onClose} data-testid="snap-open-inbox"><Inbox className="h-4 w-4 mr-1" /> Open Receipt inbox</Link></Button>
          </div>
        ) : (
          <div className="space-y-3">
            <img src={preview} alt="Receipt" className="w-full max-h-56 object-contain bg-white rounded-xl border" data-testid="snap-preview" />
            {progress !== null ? <p className="text-sm font-medium" data-testid="snap-progress">Reading receipt… {progress}%</p> : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2"><Label>Store</Label><Input value={f.store} onChange={(e) => setF({ ...f, store: e.target.value })} data-testid="snap-store-input" /></div>
                  <div><Label>Total</Label><Input type="number" step="0.01" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="num" data-testid="snap-amount-input" /></div>
                  <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="snap-date-input" /></div>
                  <div className="col-span-2"><Label>Note (optional)</Label><Input value={f.note} placeholder="e.g. lunch with client" onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="snap-note-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="ghost" onClick={reset} data-testid="snap-retake-button">Retake</Button>
                  <Button onClick={save} data-testid="snap-save-button"><Plus className="h-4 w-4 mr-1" /> Save to inbox</Button>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Always-visible camera button on phones (hidden on desktop and on the lock screen)
export function SnapFab() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="md:hidden fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl grid place-items-center active:scale-95 transition-transform" aria-label="Snap a receipt" data-testid="snap-fab">
        <Camera className="h-6 w-6" />
      </button>
      {open && <SnapDialog onClose={() => setOpen(false)} />}
    </>
  );
}
